/**
 * COMPACT TEMPLATE — EducationSection
 *
 * ⚠️  DO NOT add data-splitable-item / data-splitable-header attributes here.
 * The compact template uses CSS-based page splitting, not the React
 * usePageDelimitation system. Adding split attributes will break the
 * sidebar/main layout by pushing content to wrong pages.
 *
 * History: commit dc115433 added chunk slicing + data-splitable attributes
 * to this component as part of wiring usePageDelimitation into compact.
 * This broke the split — reverted to the simple un-chunked version.
 */
import { type CVEditingSection, type FullCV } from '@cibli/shared';

import { useCVTranslation } from '@/features/cv/hooks/useCVTranslation.ts';

import EditableSection from '../../../components/form/EditableSection';
import { isNotEmpty } from '../../../utils/is-not-empty';
import { formatDateRange } from './helpers';

interface EducationSectionProps {
  education: FullCV['education'];
  onEdit?: (section: CVEditingSection) => void;
}

export default function EducationSection({ education, onEdit }: EducationSectionProps) {
  const { t } = useCVTranslation();
  const editable = onEdit !== undefined;

  if (!education?.length && !editable) return null;

  const handleEdit = () => {
    if (onEdit) onEdit('education');
  };

  return (
    <EditableSection onEdit={handleEdit} editable={editable} splitable>
      <div className="mb-1 mx-0 px-0">
        <h2 className="text-[11px] font-black uppercase tracking-widest mb-0.25">
          {t('CV.education')}
        </h2>
        {(education ?? []).map((edu, index) => {
          const dateRange = formatDateRange(edu.degreeStartedAt, edu.degreeObtainedAt, t);

          return (
            <div key={index} className="mb-2 break-inside-avoid [page-break-inside:avoid]">
              <div className="flex items-baseline justify-between gap-2 leading-tight">
                <div className="flex flex-wrap items-baseline gap-x-1.5 min-w-0">
                  {isNotEmpty(edu.degree) && (
                    <h3 className="text-[11px] font-black uppercase text-black">
                      {edu.degree?.toUpperCase()}
                    </h3>
                  )}
                  {isNotEmpty(edu.degree) && isNotEmpty(edu.name) && (
                    <span className="text-[11px] text-gray-400">-</span>
                  )}
                  {isNotEmpty(edu.name) && <p className="text-[11px] font-semibold">{edu.name}</p>}
                </div>
                {dateRange && (
                  <p className="text-[10.75px] text-gray-600 italic whitespace-nowrap flex-shrink-0">
                    {dateRange}
                  </p>
                )}
              </div>
              {isNotEmpty(edu.school) && <p className="text-[11px] text-black">{edu.school}</p>}
            </div>
          );
        })}
      </div>
    </EditableSection>
  );
}
